import { useEffect } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import { Accordion } from '@iwoca/orion';
import { FormattedMessage } from 'react-intl';

import { useStateKey } from '../../../../../../../iwQueryClient/useStateKey';
import { DragDropUpload } from '../../../../../../ui/documentRequirement/dragDropUpload/DragDropUpload';
import { FileStatusSections } from '../../../../../../ui/documentRequirement/fileStatusSections/FileStatusSections';
import { useFileUploadReducer } from '../../../../../../ui/documentRequirement/hooks/useFileUploadReducer';
import { useGetDocuments } from '../../../../../../ui/documentRequirement/hooks/useGetDocuments';

const DOCUMENT_TYPE = 'bank statement';

const listOfInstructions = [
    <FormattedMessage
        key="download"
        id="_.funding_requirements.bank_evidence.manual_bs_upload.guidance_download"
        defaultMessage="You can download these from your online banking (<link>See instructions</link>)."
        values={{
            link: (text) => (
                <a
                    href="https://static.iwoca.com/pdf/iwoca_bank_statement_pdf_download_guide.pdf"
                    target="_blank"
                    rel="noreferrer"
                    className="underline"
                >
                    {text}
                </a>
            )
        }}
    />,
    <FormattedMessage
        key="formats"
        id="_.funding_requirements.bank_evidence.manual_bs_upload.guidance_formats"
        defaultMessage="Files must be PDF or JPG, JPEG, PNG and no more than 2GB."
    />,
    <FormattedMessage
        key="revenue"
        id="_.funding_requirements.bank_evidence.manual_bs_upload.guidance_2"
        defaultMessage="Statements must be from the account(s) where the majority of your business revenue is received."
    />,
    <FormattedMessage
        key="details"
        id="_.funding_requirements.bank_evidence.manual_bs_upload.guidance_3"
        defaultMessage="Statements must show the business name, sort code, account number, and account balance."
    />
];

export const BankStatementUpload = () => {
    const stateKey = useStateKey();
    const { data } = useGetDocuments(stateKey);

    const {
        uploadedFiles,
        setAllFiles,
        setFileLoading,
        setFileSuccess,
        setFileError,
        setFileReject,
        removeFile
    } = useFileUploadReducer();

    useEffect(() => {
        if (!data?.data) return;

        const bankStatementDocuments = data.data.filter(
            // @ts-ignore — lapi-client types the array element as never
            (document) => document.attributes?.document_type === DOCUMENT_TYPE
        );
        setAllFiles(bankStatementDocuments);
        // Seed once from the server; local uploads own the state afterwards.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    return (
        <div className="rounded-s bg-secondary-95">
            <Accordion.Item variant="plain" size="small">
                <Accordion.Header>
                    <Accordion.Title className="text-s font-med">
                        <FormattedMessage
                            id="_.funding_requirements.bank_evidence.manual_bs_upload.accordion_title"
                            defaultMessage="If you prefer, you can upload your latest 6 months of bank statements"
                        />
                    </Accordion.Title>
                    <Accordion.Trigger />
                </Accordion.Header>
                <Accordion.Content>
                    <div className="flex flex-col gap-l">
                        <DragDropUpload
                            documentType={DOCUMENT_TYPE}
                            listOfInstructions={listOfInstructions}
                            setFileLoading={(id, name) => {
                                tracking.ampli.uploadBankStatementsClicked({
                                    version: '2.0'
                                });
                                setFileLoading(id, name);
                            }}
                            setFileSuccess={setFileSuccess}
                            setFileError={setFileError}
                            setFileReject={setFileReject}
                        />
                        <FileStatusSections
                            files={uploadedFiles}
                            removeFile={removeFile}
                        />
                    </div>
                </Accordion.Content>
            </Accordion.Item>
        </div>
    );
};
